import { dealMatchesPrice } from '@share-a-cart/shared-utils';
import { parseCartAmazonDeal } from '../../api/getCartDealsAPI';
import type { CartAmazonDeal } from '../../types/deals';
import { isAmazonLocalCart } from '../../utils/get/sendCart/amazonChunkUtils';
import { isShowableAmazonOffer, isShowableAmazonPrice } from './amazonItemFilters';

export function amazonAsinKey(asin: string | undefined): string {
  return (asin ?? '').trim().toUpperCase();
}

function textOf(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function cartDealsEnabled(
  page: 'get' | 'embed' | 'review' | 'receive',
  cart: { vendor?: string; store?: string } | undefined,
): boolean {
  return page !== 'embed' && cart?.vendor === 'amazon' && !isAmazonLocalCart(cart?.store);
}

export function liveCartDeals(query: {
  isError: boolean;
  data?: Record<string, CartAmazonDeal | null>;
}): Record<string, CartAmazonDeal | null> | undefined {
  return query.isError ? undefined : query.data;
}

/** Must stay in step with shownCartDeal in website/js/share.js. */
export function cartRowDeal(
  deal: CartAmazonDeal | null | undefined,
  rowPrice: unknown,
  rowPriceAsOf: number | undefined,
  now: number,
): CartAmazonDeal | null {
  // A badge is current offer data, so it sits only beside a price Amazon reported within the on-screen limit.
  if (!deal || rowPriceAsOf == null || !isShowableAmazonPrice(rowPriceAsOf, now)) return null;
  if (!isShowableAmazonOffer(deal.asOf, deal.endTime, now)) return null;
  return dealMatchesPrice(deal.price, rowPrice) ? deal : null;
}

export function cartHasPrimeDeal<T>(
  items: T[] | undefined,
  dealForItem: ((item: T) => CartAmazonDeal | null) | undefined,
): boolean {
  return !!dealForItem && !!items?.some((item) => dealForItem(item)?.state === 'prime');
}

// The cart overlays each Amazon record with its price and priceAsOf, and a deal carries its record's priceAsOf as asOf.
export function cartAwaitsNewRecords(
  items: ReadonlyArray<{ asin?: string; price?: unknown; priceAsOf?: number }>,
  deals: Record<string, CartAmazonDeal | null> | undefined,
  now: number,
): boolean {
  return items.some((item) => {
    const deal = deals?.[amazonAsinKey(item.asin)];
    return (
      !!deal &&
      item.priceAsOf !== deal.asOf &&
      cartRowDeal(deal, item.price, item.priceAsOf, now) === null &&
      cartRowDeal(deal, deal.price, deal.asOf, now) !== null
    );
  });
}

/** Must stay in step with rowDealBasis in website/js/share.js. */
export function rowDealBasis(
  deal: CartAmazonDeal | null | undefined,
  shownPrice: unknown,
): number | null {
  const amount = deal?.basisAmount;
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  const price =
    typeof shownPrice === 'number' ? shownPrice : Number.parseFloat(String(shownPrice ?? ''));
  return price > 0 && Math.round(amount * 100) > Math.round(price * 100) ? amount : null;
}

export type SearchDeal = {
  deal: CartAmazonDeal | null;
  asOf?: number;
};

export function mergeSearchDeals(
  previous: Record<string, SearchDeal>,
  results: ReadonlyArray<Record<string, unknown>>,
): Record<string, SearchDeal> {
  return results.reduce<Record<string, SearchDeal>>(
    (deals, result) => {
      const asin = amazonAsinKey(textOf(result.asin) ?? textOf(result.itemid));
      const deal = parseCartAmazonDeal(result.deal);
      if (asin) {
        deals[asin] = {
          deal,
          asOf: typeof result.priceAsOf === 'number' ? result.priceAsOf : deal?.asOf,
        };
      }
      return deals;
    },
    { ...previous },
  );
}

// Cart deals refetch while a search entry never does, so a cart deal, or a null, read after the search replaces it.
// A null carries no record time, so it counts from when the cart deals were fetched.
export function pickDeal(
  searchDeals: Record<string, SearchDeal>,
  cartDeals: Record<string, CartAmazonDeal | null> | undefined,
  cartDealsFetchedAt: number,
  asin: string,
): CartAmazonDeal | null | undefined {
  const key = amazonAsinKey(asin);
  const cartDeal = cartDeals?.[key];
  const searched = Object.prototype.hasOwnProperty.call(searchDeals, key)
    ? searchDeals[key]
    : undefined;
  if (!searched) return cartDeal;
  if (cartDeal === undefined) return searched.deal;
  const cartAsOf = cartDeal ? cartDeal.asOf : cartDealsFetchedAt;
  return cartAsOf > (searched.asOf ?? -Infinity) ? cartDeal : searched.deal;
}
